// ✅ src/pages/Asset/AssetTypeMaster.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaPlus, FaSave } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/typeEntry";

const defaultForm = {
  id: 0,
  assetTypeName: "",
  assetTypeCode: "",
  color: "#ffffff",
  remarks: "",
  index: "",
  vehicle: false,
  otherAsset: false,
  land: false,
  shutteringMaterials: false,
  isMovable: false,
  active: true,
};

const AssetTypeMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();
  const location = useLocation();

  const qs = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const editId = useMemo(() => Number(qs.get("id") || 0), [qs]);
  const isEdit = !!editId;

  const [formData, setFormData] = useState({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [isLockedByTrans, setIsLockedByTrans] = useState(false);

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId],
  );

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const requiredClass = "text-red-500";

  const selectTypeExclusive = (name, checked) => {
    if (!checked) {
      setFormData((p) => ({ ...p, [name]: false }));
      return;
    }

    setFormData((p) => ({
      ...p,
      vehicle: name === "vehicle",
      otherAsset: name === "otherAsset",
      land: name === "land",
      shutteringMaterials: name === "shutteringMaterials",
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (
      ["vehicle", "otherAsset", "land", "shutteringMaterials"].includes(name)
    ) {
      if (isLockedByTrans) return;
      return selectTypeExclusive(name, checked);
    }

    if (isLockedByTrans && (name === "active" || name === "isMovable")) return;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleBack = () => navigate("/Asset/AssetMainTypeListing");

  const resetForNew = () => {
    setFormData({ ...defaultForm });
    setIsLockedByTrans(false);
    toast.info("Form reset for new entry");
  };

  // -------------------------
  // API: Load Edit Data
  // -------------------------
  const loadEdit = async (id) => {
    try {
      setLoading(true);

      const rs = await axios.get(`${API_BASE}/${id}`, {
        headers: commonHeaders,
      });
      const data = rs.data?.data;

      if (!data) {
        toast.error("Record not found");
        return;
      }

      setFormData((p) => ({
        ...p,
        id: id,
        assetTypeName: data.assetTypeName ?? "",
        assetTypeCode: data.assetTypeCode ?? "",
       color: (data.color?.startsWith("#")
  ? data.color
  : data.color
    ? `#${data.color}`
    : "#ffffff"
).toLowerCase(),
        remarks: data.remarks ?? "",
        index: String(data.index ?? ""),
        vehicle: !!data.vehicle,
        otherAsset: !!data.otherAsset,
        land: !!data.land,
        shutteringMaterials: !!data.shutteringMaterials,
        isMovable: !!data.isMovable,
        active: !!data.active,
      }));

      const tx = await axios.get(`${API_BASE}/transExist/${id}`, {
        headers: commonHeaders,
      });

      const locked = !!tx.data?.transExists;
      setIsLockedByTrans(locked);

      if (locked) {
        toast.info(
          "Transactions exist. Type/Active/Movable fields are locked.",
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load edit data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) loadEdit(editId);
    else resetForNew();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // -------------------------
  // Validation
  // -------------------------
  const validate = () => {
    if (!formData.assetTypeName.trim()) return "Asset Type Name is required!";
    if (!formData.assetTypeCode.trim()) return "Asset Type Code is required!";
    if (!String(formData.color || "").trim()) return "Color is required!";
    if (!String(formData.index || "").trim()) return "Index is required!";

    const picked =
      formData.vehicle ||
      formData.otherAsset ||
      formData.land ||
      formData.shutteringMaterials;

    if (!picked) {
      return "Please select Asset Type (Vehicle / Other Asset / Land / Shuttering Materials)";
    }

    if (Number.isNaN(Number(formData.index))) return "Index must be a number";

    return null;
  };

  const checkUniques = async () => {
    try {
      const mode = isEdit ? "Update" : "Save";

      const rs = await axios.post(
        `${API_BASE}/checkUniques`,
        {
          mode,
          id: isEdit ? editId : 0,
          assetTypeCode: formData.assetTypeCode,
          assetTypeName: formData.assetTypeName,
          color: formData.color,
          index: formData.index,
        },
        {
          headers: commonHeaders,
        },
      );

      if (rs.data?.valid === false) {
        const errs = rs.data?.errors || [];
        if (errs.length) {
          toast.error(errs[0]);
          return false;
        }
      }

      return true;
    } catch (e) {
      return true;
    }
  };

  // -------------------------
  // Save / Update
  // -------------------------
  const handleSave = async () => {
    const err = validate();
    if (err) return toast.error(err);

    const ok = await checkUniques();
    if (!ok) return;

    try {
      setLoading(true);

      const payload = {
        ...formData,
        id: isEdit ? editId : 0,
        loginUserId: userId || 0,
      };

      await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      console.log("payload:", payload);

      toast.success(isEdit ? "Updated Successfully" : "Saved Successfully");
      navigate("/Asset/AssetMainTypeListing");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AssetLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">ASSET TYPE MASTER</span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />
        <button
          onClick={handleBack}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
        >
          <FaArrowLeft />
          Back
        </button>
        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <h1
                className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                {isEdit
                  ? `Edit Asset Type (ID: ${editId})`
                  : "Asset Type Master"}
              </h1>

              {isLockedByTrans && (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    isDark
                      ? "bg-yellow-900 text-yellow-100"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  Locked (Transactions exist)
                </span>
              )}
            </div>

            <div className="flex items-center gap-3"></div>
          </div>

          <div
            className={`rounded-xl border p-6 ${
              isDark
                ? "border-gray-700 bg-gray-800/50"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Asset Type Name <span className={requiredClass}>*</span>
                </label>
                <input
                  type="text"
                  name="assetTypeName"
                  value={formData.assetTypeName}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Enter asset type name"
                  disabled={loading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Asset Type Code <span className={requiredClass}>*</span>
                </label>
                <input
                  type="text"
                  name="assetTypeCode"
                  value={formData.assetTypeCode}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Enter asset type code"
                  disabled={loading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Color <span className={requiredClass}>*</span>
                </label>

                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    className={`flex-1 ${inputClass}`}
                    placeholder="#RRGGBB"
                    disabled={loading}
                  />
                  <input
  type="color"
  value={
    /^#[0-9a-fA-F]{6}$/.test(formData.color)
      ? formData.color.toLowerCase()
      : "#ffffff"
  }
  onChange={(e) =>
    setFormData((p) => ({ ...p, color: e.target.value.toLowerCase() }))
  }
  className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
  disabled={loading}
  title="Pick color"
/>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                <label
                  className={`${labelClass} text-right md:text-right pt-2`}
                >
                  Remarks
                </label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  rows={4}
                  className={inputClass}
                  placeholder="Enter remarks"
                  disabled={loading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Index <span className={requiredClass}>*</span>
                </label>
                <input
                  type="text"
                  name="index"
                  value={formData.index}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Enter index"
                  disabled={loading}
                />
              </div>

              <div className="border-t pt-6 mt-6 border-gray-200 dark:border-gray-700">
<div className="text-center mb-4 font-semibold">
  Select exactly one Asset Type <span className={requiredClass}>*</span>
</div>

                <div className="flex flex-wrap justify-center gap-8 mb-6">
                  {[
                    { key: "vehicle", label: "Vehicle" },
                    { key: "otherAsset", label: "Other Asset" },
                    { key: "land", label: "Land" },
                    {
                      key: "shutteringMaterials",
                      label: "Shuttering Materials",
                    },
                  ].map((x) => (
                    <label
                      key={x.key}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        name={x.key}
                        checked={formData[x.key]}
                        onChange={handleChange}
                        disabled={loading || isLockedByTrans}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span
                        className={isDark ? "text-gray-300" : "text-gray-700"}
                      >
                        {x.label}
                      </span>
                    </label>
                  ))}
                </div>

                <div className="flex flex-wrap justify-center gap-8">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isMovable"
                      checked={formData.isMovable}
                      onChange={handleChange}
                      disabled={loading || isLockedByTrans}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span
                      className={isDark ? "text-gray-300" : "text-gray-700"}
                    >
                      IsMovable
                    </span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="active"
                      checked={formData.active}
                      onChange={handleChange}
                      disabled={loading || isLockedByTrans}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span
                      className={isDark ? "text-gray-300" : "text-gray-700"}
                    >
                      Active
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  <FaSave />
                  {isEdit ? "Update" : "Save"}
                </button>

                <button
                  onClick={resetForNew}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : `${buttonClass} ${hoverClass} text-white`
                  }`}
                >
                  <FaPlus />
                  Add New
                </button>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetTypeMaster;
